import React, { useEffect, useRef } from 'react';

// Google reCAPTCHA v2 checkbox widget for public forms (Contact, product
// enquiry, Register) — stops bot/spam submissions. Renders nothing until
// the site owner sets REACT_APP_RECAPTCHA_SITE_KEY (see .env.example) so
// forms keep working before that's configured; the backend mirrors this by
// skipping verification when its own key isn't set.
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

export default function Captcha({ onToken }) {
  const boxRef = useRef();
  const widgetId = useRef(null);

  useEffect(() => {
    if (!SITE_KEY) return;

    // grecaptcha.render exists as soon as the outer script tag finishes
    // loading, but the library isn't actually ready to render a widget
    // until grecaptcha.ready() fires — calling render() before that
    // silently no-ops. Poll for the render function, then gate through
    // ready() as Google's own docs prescribe.
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.grecaptcha || !window.grecaptcha.render) { setTimeout(tryRender, 200); return; }
      window.grecaptcha.ready(() => {
        if (cancelled || !boxRef.current || widgetId.current !== null) return;
        widgetId.current = window.grecaptcha.render(boxRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
        });
      });
    };

    if (window.grecaptcha && window.grecaptcha.render) {
      tryRender();
    } else {
      let script = document.getElementById('recaptcha-script');
      if (!script) {
        script = document.createElement('script');
        script.id = 'recaptcha-script';
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener('load', tryRender);
    }
    return () => { cancelled = true; };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={boxRef} style={{ margin: '10px 0' }} />;
}
